// @flow
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import {CancelOutlined, InfoOutlined, PublishRounded} from '@material-ui/icons';

import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {colors} from '../../../../../assets/styles/colors';
import {validateVideoUrl} from '../../../../products/utils';
import {Tooltip} from '@material-ui/core';
import {useIsFeatureInTier} from '../../../../../utils/component';
import CropModal from '../../../../../elements/CropModal';

import type {StoreInformation} from '../../../../../entities';
import type {ImageToCrop} from '../../../../../elements/CropModal';

type Props = {
  storeInformation?: StoreInformation,
  register: (val: any) => void,
  errors: {[key: string]: any},
  setValue: (field: string, value: any) => void,
  profileImage?: string | null,
  setProfileImage: (img: string | null) => void,
  setProfileImageFile: (f: File | null) => void,
  isValidUrl: boolean,
  setIsValidUrl: (val: boolean) => void,
  setVideoSocialMediaTypeId: (val: string | typeof undefined) => void,
};

const useStyles = makeStyles(theme => ({
  descriptionText: {
    fontSize: '0.875rem',
    color: colors.grey800,
    marginBottom: theme.spacing(2),
  },
  tooltip: {
    borderRadius: '6px',
    background: colors.grey200,
    color: colors.grey800,
  },
  subtitle: {
    fontWeight: 500,
    fontSize: 14,
    marginBottom: theme.spacing(1),
    color: colors.grey900,
  },
  middleSubtitle: {
    marginTop: theme.spacing(2),
  },
  imageBoxDashed: {
    height: '100%',
    border: `2px dashed ${colors.grey500}`,
    boxSizing: 'border-box',
    borderRadius: '12px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: colors.purple3,
    '&:focus-within': {
      border: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  defaultCursor: {
    cursor: 'unset',
  },
  imageBox: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #E8E8E9',
    boxSizing: 'border-box',
    borderRadius: theme.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    // height: 145,
  },
  image: {
    margin: 'auto',
    borderRadius: '50%',
    maxWidth: '100%',
  },
  removeImageIcon: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
  imageUploadTip: {
    textAlign: 'center',
    // fontStyle: 'italic',
    fontSize: '0.875rem',
    color: colors.purple3,
  },
  disabledTextColor: {
    color: colors.grey600,
  },
  borderTooltip: {
    color: colors.grey600,
  },
}));

export default function AboutUsSection(props: Props) {
  const {
    storeInformation,
    register,
    errors,
    setValue,
    profileImage,
    setProfileImage,
    setProfileImageFile,
    isValidUrl,
    setIsValidUrl,
    setVideoSocialMediaTypeId,
  } = props;
  const classes = useStyles();

  const [storePhoto, setStorePhoto] = useState<ImageToCrop | null>(null);

  const handleProfileFileInputChange = async (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const file = event.target.files[0];
    if (!file) return;

    setStorePhoto({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleRemoveProfileImage = () => {
    setProfileImage(null);
    setProfileImageFile(null);
  };

  const evaluateVideoUrl = (str: string) => {
    if (str === '') {
      setIsValidUrl(true);
      return;
    }
    const validUrl = validateVideoUrl(str);
    if (validUrl) {
      const {videoType, embed} = validUrl;
      setVideoSocialMediaTypeId(videoType);
      setValue('videoUrl', embed);
      setIsValidUrl(true);
    } else {
      setVideoSocialMediaTypeId(undefined);
      setIsValidUrl(false);
    }
  };

  const handleCheckVideoUrl = e => {
    setIsValidUrl(false);
    evaluateVideoUrl(e.target.value);
  };

  const hasProfileInfoFeature = useIsFeatureInTier('STORE_PROFILE_INFORMATION');

  let profileImageField;
  if (profileImage !== null) {
    profileImageField = (
      <div className={classes.imageBox}>
        <img className={classes.image} src={profileImage} alt="" />
        <IconButton
          aria-label={'cancel'}
          className={classes.removeImageIcon}
          onClick={handleRemoveProfileImage}
          //   disabled={isSaving || !hasProfileInfoFeature}
        >
          <CancelOutlined />
        </IconButton>
      </div>
    );
  } else {
    profileImageField = (
      <label
        className={`${classes.imageBoxDashed} ${
          !hasProfileInfoFeature ? classes.defaultCursor : ''
        }`}
        aria-label="Subir tu foto o foto de equipo"
        // onClick={handleAddProfileImageClick}
      >
        <PublishRounded
          color={hasProfileInfoFeature ? 'primary' : 'disabled'}
        />
        <Typography
          className={`${classes.imageUploadTip} ${
            !hasProfileInfoFeature ? classes.disabledTextColor : ''
          }`}
        >
          Sube tu foto aquí (350 x 350 px)
        </Typography>
        <input
          // ref={profileFileInputRef}
          hidden
          type="file"
          id="store-profile-input-file"
          accept="image/png, image/jpeg, image/webp"
          onChange={handleProfileFileInputChange}
          disabled={!hasProfileInfoFeature}
        />
      </label>
    );
  }

  return (
    <div>
      <Typography className={classes.descriptionText}>
        Preséntate ante tu cliente, conocer a la persona detrás del
        emprendimiento genera mayor confianza al momento de la compra.
        <br />
        Te recomendamos grabar un video en formato TikTok o Youtube y colocar el
        link.
        <Tooltip
          // open
          classes={{tooltip: classes.tooltip}}
          title={
            <>
              Algunas recomendaciones para el video:
              <ul
                style={{
                  marginBlockStart: '4px',
                  marginBlockEnd: 0,
                  paddingInlineStart: '16px',
                }}
              >
                <li>Déjalo fluír y que se vea natural.</li>
                <li>Cuenta tu historia, tu propósito de negocio.</li>
                <li>No debe durar más de 1 minuto.</li>
                <li>Sonríe.</li>
              </ul>
            </>
          }
          placement="left-start"
        >
          <InfoOutlined className={classes.borderTooltip} />
        </Tooltip>
      </Typography>
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          md={4}
          style={{display: 'flex', flexDirection: 'column'}}
        >
          <Typography
            className={classes.subtitle}
            gutterBottom
            id="team-picture"
          >
            Tu foto o foto de equipo:
          </Typography>
          {profileImageField}
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography
            className={classes.subtitle}
            gutterBottom
            id="team-description"
          >
            Breve introducción:
          </Typography>
          <CustomTextField
            fullWidth
            multiline
            id="store-profile-introduction"
            name="description"
            autoComplete="store-profile-description"
            error={!!errors.description}
            helperText={errors.description ? errors.description.message : ''}
            defaultValue={
              storeInformation && storeInformation.profileInformation
                ? storeInformation.profileInformation.description
                : ''
            }
            type="text"
            inputRef={register()}
            minRows={4}
            // disabled={isSaving || !hasProfileInfoFeature}
            className={classes.descriptionWrap}
          />
          <Typography
            className={`${classes.subtitle} ${classes.middleSubtitle}`}
            gutterBottom
            id="team-video-link"
          >
            Link de video:
          </Typography>
          <CustomTextField
            id="store-profile-introduction-video"
            name="videoUrl"
            autoComplete="product-videoUrl"
            error={!!errors.videoUrl}
            helperText={errors.videoUrl ? errors.videoUrl.message : ''}
            defaultValue={
              storeInformation && storeInformation.profileInformation
                ? storeInformation.profileInformation.videoUrl
                : ''
            }
            type="text"
            inputRef={register()}
            // disabled={isSaving || !hasProfileInfoFeature}
            className={classes.productInputField}
            onChange={handleCheckVideoUrl}
          />
          {!isValidUrl && (
            <Typography color="error" hidden={isValidUrl}>
              Link de video inválido
            </Typography>
          )}
        </Grid>
      </Grid>

      {storePhoto && (
        <CropModal
          open
          images={[storePhoto]}
          onClose={() => setStorePhoto(null)}
          compressOptions={{
            quality: 0.5,
            maxWidth: 3840,
            maxHeight: 3840,
          }}
          onSubmit={result => {
            const [cropResult] = result;
            setProfileImage(cropResult.preview);
            setProfileImageFile(cropResult.file);
            setStorePhoto(null);
          }}
        />
      )}
    </div>
  );
}

AboutUsSection.propTypes = {
  storeInformation: PropTypes.object,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  profileImage: PropTypes.string,
  setProfileImage: PropTypes.func.isRequired,
  setProfileImageFile: PropTypes.func.isRequired,
  isValidUrl: PropTypes.bool.isRequired,
  setIsValidUrl: PropTypes.func.isRequired,
  setVideoSocialMediaTypeId: PropTypes.func.isRequired,
};
